import { NextPage } from "next"
import NBAPageWrapper from "../../../layouts/nba-page-wrapper/views/NBAPageWrapper"
import staticData from "../models/staticData"

import styles from "../assets/css/index.module.scss"

const PrivacyPolicy: NextPage = () => {
	return (
		<NBAPageWrapper {...staticData}>
			<main className={`${styles.mainContainer}`}>
				<div className="mx-auto max-xxl">
					<PrivacyPolicyText />
				</div>
			</main>
		</NBAPageWrapper>
	)
}

export const PrivacyPolicyText = () => (
	<div className={styles.privacyPolicyText}>
		<h1 className="wp-block-heading">Privacy Policy</h1>

		<p>Last Updated: March 16, 2026&nbsp;</p>

		<p>
			This Privacy Policy (“<strong>Privacy Policy</strong>”) describes how Memphis Basketball, LLC,
			Forum Management LLC, together with their parents, affiliates, and subsidiaries (collectively,
			“<strong>Memphis Arena Affiliates</strong>,”“<strong>we</strong>,” “<strong>our</strong>,” or
			“<strong>us</strong>”), may collect, use, and share your personal information, and the choices
			you have with respect to your personal information, in connection with your use of the web
			properties that we own or operate that link to this Privacy Policy (each, a “
			<strong>Website</strong>”) and our mobile applications that link to this Privacy Policy (when
			using in FedExForum, Grizzlies, or Grind City Media mode, each, an “<strong>App</strong>”),
			and when visiting FedExForum or attending other entertainment experiences, youth programs, or
			events we sponsor or operate (each, an “<strong>Event</strong>,” and together with the
			Websites and App, the “<strong>Platform</strong>”).&nbsp;
		</p>

		<p>
			The Platform provides individual end users (“<strong>Users</strong>”) with the ability to
			access material, information, and content (collectively, “<strong>Content</strong>”) and our
			related online and offline services, social media pages, email, and other electronic
			communications (collectively, “<strong>Services</strong>”) (the Content and Services are
			incorporated into the Platform for purposes of this Privacy Policy). Finally, Users may be
			able to purchase merchandise and other products (collectively, “<strong>Products</strong>
			”) on the Platform.&nbsp;
		</p>

		<p>
			Memphis Arena Affiliates are the owner and operator of FedExForum, Grind City Media, Grizz
			Gaming, and the Memphis Grizzlies, and the affiliated NBA G-League team, the Memphis Hustle
			(collectively, our “<strong>Related Entities</strong>”). As a result, we may share personal
			information across our Related Entities to improve our Products and Services, provide you with
			tailored Content, and for other purposes described in this Privacy Policy, or at the time of
			collection.&nbsp;
		</p>

		<p>
			We may also partner with third parties (“<strong>Event Partners</strong>”) to provide certain
			Events and related services. This Privacy Policy, however, only governs the collection, use,
			and sharing of information by Memphis Arena Affiliates in connection with the Platform. We
			encourage you to review the privacy policies published or otherwise presented to you in
			connection with any online service or event with which you engage to understand how your
			personal information may be collected and used.
		</p>

		<p>
			The Memphis Grizzlies is a Member Club of the National Basketball Association (the “
			<strong>League</strong>”). Please also note that the Memphis Grizzlies and Memphis Hustle
			Websites are part of the NBA.com website and the NBA mobile app. As a result, we may receive
			your personal information from, and share your personal information with, the League and its
			other Member Clubs. Memphis Arena Affiliates do not have any control over, and we are not
			responsible for, the League’s or its other Member Clubs’ data practices and privacy policies.
			Please review the League’s privacy policy available at&nbsp;
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://www.nba.com/privacy-policy&amp;g=YjQ3MmI1YTU2YzgwMWU3YQ==&amp;h=NTk2Yzg4MDU5ZTg3OTliZThiZjVjMTdlY2IwMGRjYTRmMjBiZWJmODc0YjBiZmE1ODEyYTQxYzVlMDczNzJjYQ==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				www.nba.com/privacy-policy
			</a>
			&nbsp;to learn about the League’s data practices and the choices you may have with respect to
			your information that is processed by the League.&nbsp;
		</p>

		<p>
			Memphis Arena Affiliates reserve the right, at any time, to modify this Privacy Policy. If we
			modify how we collect, use, or share personal information, we will post the updated Privacy
			Policy on this page. In some cases, we may notify you about an update by sending you an email,
			posting a notice about the update on the Platform, or other means as may be required by
			applicable law. In all cases, your continued use of the Platform following posting or other
			notification of changes constitutes your acknowledgement of such changes. Please review this
			Privacy Policy periodically to keep up to date on our most current policies and
			practices.&nbsp;
		</p>

		<p>
			This Privacy Policy covers the following topics. In some cases, region-specific terms may
			apply to you, which are indicated in the section headings below.
		</p>

		<div className="anchors">
			<p>
				<a href="#COLLECTION-OF-PERSONAL-INFORMATION">COLLECTION OF PERSONAL INFORMATION</a>
			</p>

			<p>
				<a href="#USE-OF-AND-PURPOSES-FOR-PROCESSING-PERSONAL-INFORMATION">
					USE OF AND PURPOSES FOR PROCESSING PERSONAL INFORMATION
				</a>
			</p>

			<p>
				<a href="#DISCLOSURE-OF-PERSONAL-INFORMATION">DISCLOSURE OF PERSONAL INFORMATION</a>
			</p>

			<p>
				<a href="#INTERNATIONAL-TRANSFERS-OF-PERSONAL-INFORMATION">
					INTERNATIONAL TRANSFERS OF PERSONAL INFORMATION
				</a>
			</p>

			<p>
				<a href="#SECURITY-OF-PERSONAL-INFORMATION">SECURITY OF PERSONAL INFORMATION</a>
			</p>

			<p>
				<a href="#YOUR-CHOICES">YOUR CHOICES</a>
			</p>

			<p>
				<a href="#THIRD-PARTY-SITES-AND-SERVICES">THIRD PARTY SITES AND SERVICES</a>
			</p>

			<p>
				<a href="#ADDITIONAL-NOTICE-TO-RESIDENTS-OF-APPLICABLE-U.S.-STATES">
					ADDITIONAL NOTICE TO RESIDENTS OF APPLICABLE U.S. STATES
				</a>
			</p>

			<p>
				<a href="#CONTACTING-US">CONTACTING US</a>
			</p>
		</div>

		<h2 className="wp-block-heading" id="COLLECTION-OF-PERSONAL-INFORMATION">
			<strong>COLLECTION OF PERSONAL INFORMATION</strong>
		</h2>

		<p>
			<strong>Personal Information Collected</strong>.The personal information we collect, and how
			we use it, depends largely upon how you engage with the Platform, and may include the
			following categories of personal information. We may also collect other personal information
			that is not specifically listed here, which we will use as described in this Privacy Policy or
			as otherwise disclosed at the time of collection.
		</p>

		<ul className="wp-block-list dot">
			<li>
				<strong>Contact information</strong>, such as your first and last name, email address, phone
				number, mailing address, and social media handle.
			</li>

			<li>
				<strong>Professional information</strong>, such as your job title, your organization’s name
				and location, and related details about your business or profession.
			</li>

			<li>
				<strong>Account information </strong>(if applicable to the Services), such as your log-in
				details, information that you store in your account, information you provide during
				registration, ticket holder status, fan profile information, and other details about your
				use of the Platform. 
			</li>

			<li>
				<strong>Payment information</strong>, such as payment amounts and payment methods used to
				complete transactions, including in-venue or through ticket platforms and box offices.
				Payment processing services connected with your use of the Platform may be provided to you
				by us or a third-party payment processor. 
			</li>

			<li>
				<strong>Transaction history</strong>, such as records of Products and Services you have
				purchased from us, our Related Entities, or Event Partners, including ticket purchase data,
				in-venue data (e.g., time spent and use of WiFi), and other details about your transactions
				through the Platform or at an Event. Certain of this information may be captured through the
				use of digital ticketing or RFID technologies.
			</li>

			<li>
				<strong>Demographic information</strong>, such as gender, age/date of birth, household
				income, marital status, military or veteran status, education, occupation, lifestyle, and
				employment history.
			</li>

			<li>
				<strong>Preferences</strong>, such as any marketing or communications preferences, language
				preferences, team preferences, fan engagement, Content preferences, purchasing behavior,
				consumer research information (e.g., concert genre affinities), and details about how you
				engage with our communications or the Platform.
			</li>

			<li>
				<strong>Survey responses</strong>, such as the information you provide in response to our
				surveys, questionnaires, sweepstakes, or contests.
			</li>

			<li>
				<strong>Audio, electronic, or visual information</strong>, such as information collected
				from CCTV cameras and when we film or photograph you in a public location at an Event. We
				and our service providers may also record or monitor any call or chat you have with us for
				quality control or training purposes (including training AI), or to enforce our rights.
			</li>

			<li>
				<strong>Online activity information</strong>, such as the website you visited before
				browsing to the Platform, pages or screens you viewed, how long you spent on a page or
				screen, navigation paths between pages or screens, information about your activity on a page
				or screen, access times, and duration of access. 
			</li>

			<li>
				<strong>Device information</strong> such as your computer or mobile device operating system
				type and version number, wireless carrier, manufacturer and model, browser type, screen
				resolution, IP address, unique identifiers, real-time geolocation (which may include
				specific geolocation), and general location information such as city, state, or geographic
				area.
			</li>

			<li>
				<strong>Communications</strong>, such as the information associated with your requests or
				inquiries, including for support, assistance, or Event rental/licensing information, and any
				feedback you provide when you communicate with us. 
			</li>
		</ul>

		<p>
			We may also collect or create de-identified information from personal information in a manner
			that it cannot reasonably be used to infer information about, or otherwise be linked to, a
			particular individual. To the extent that we classify such information as “de-identified,” we
			commit to maintaining and using such information in a de-identified form and to not attempting
			to re-identify such information (except for purposes permitted by law).
		</p>

		<p>
			<strong>Sources of Personal Information</strong>. We obtain personal information from a
			variety of sources, including the following sources:
		</p>

		<ul className="wp-block-list dot">
			<li>
				<strong>Personal information you provide</strong>. Memphis Arena Affiliates collect personal
				information when you voluntarily submit it to us, including through offline interactions,
				such as attending an Event or visiting FedExForum, or when you fill out a form on the
				Website, participate in a survey or sweepstakes, or communicate with us.
			</li>

			<li>
				<strong>Personal information automatically collected</strong>. Memphis Arena Affiliates, our
				service providers, and our advertising partners may automatically log information about you,
				your computer, or mobile device, and your activity over time on our Platform and other sites
				and online services, such as the online activity information and device information listed
				above. Like many online services, we use cookies and similar technologies to facilitate some
				of the automatic data collection described above, which may include the following:
			</li>

			<li>
				<strong>Cookies</strong>, which are text files that websites store on a visitor’s device to
				uniquely identify the visitor’s browser or to store information or settings in the browser
				for the purpose of helping you navigate between pages efficiently, remembering your
				preferences, enabling functionality, helping us understand user activity and patterns, and
				facilitating online advertising.
			</li>

			<li>
				<strong>Flash cookies</strong>, or locally-stored objects, which are used on websites for
				purposes similar to cookies but allow storage of a larger amount of data. 
			</li>

			<li>
				<strong>Web beacons</strong>, also known as pixel tags or clear GIFs, which are typically
				used to demonstrate that a webpage or email was accessed or opened, or that certain content
				was viewed or clicked, typically to compile statistics about usage of websites and the
				success of marketing campaigns.
			</li>

			<li>
				<strong>Local storage</strong>, which is used to save data on a visitor’s device. We may use
				data from local storage to, for example, turn on web navigation, store multimedia
				preferences, customize what we show you based on your past interactions with our Platform,
				and remember your preferences. 
			</li>

			<li>
				<strong>Software development kits</strong>, or SDKs, which are third-party computer codes
				used in connection with our App for a variety of purposes, including to provide us with
				analytics regarding the use of our App, to add features or functionality to our App, or to
				facilitate online advertising. SDKs may enable third parties to collect information directly
				via our App.
			</li>

			<li>
				<strong>Personal information obtained at an Event</strong>. We collect personal information
				when you visit FedExForum or otherwise attend an Event in person, such as through video
				surveillance or RFID, and as necessary to operate the venue or Event and provide the
				requested services (including, for example, accessibility needs, medical conditions, and
				preferences), and as otherwise described in this Privacy Policy or as noticed to you
				in-venue or at an Event. 
			</li>

			<li>
				<strong>Personal information obtained from third parties</strong>. In some circumstances, we
				may collect personal information about you from third party or publicly available sources,
				such as our Event Partners, service providers, data analytics partners, operating systems
				and platforms, social media platforms, and data licensors. We may merge or combine such
				personal information with the personal information we collect from you directly or
				automatically.
			</li>

			<li>
				<strong>Personal information shared by the League</strong>. The League, through its
				subsidiary NBA Media Ventures, LLC, may share information about you with us, such as your
				contact information, demographic information, preferences, account information, and other
				information about your interactions with the League and its other Member Clubs. We may use
				this information to, for example, provide you with special offers, promotional materials,
				and other materials and information that may be relevant to you, to improve our products and
				services, and to perform internal analysis. We may merge or combine such information with
				the personal information we collect from you directly or automatically. Please visit the 
				<a href="https://checkpoint.url-protection.com/v1/url?o=https://www.nba.com/privacy-policy&amp;g=YjQ3MmI1YTU2YzgwMWU3YQ==&amp;h=NTk2Yzg4MDU5ZTg3OTliZThiZjVjMTdlY2IwMGRjYTRmMjBiZWJmODc0YjBiZmE1ODEyYTQxYzVlMDczNzJjYQ==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
					League’s privacy policy
				</a>
				 for more information on the choices the League provides you to limit the sharing of your
				information with Member Clubs, including Memphis Arena Affiliates. Memphis Arena Affiliates
				are not responsible for the privacy practices of the League or its other Member Clubs.
			</li>

			<li>
				<strong>Referrals</strong>. Users of our Platform may have the opportunity to refer to us
				prospective attendees, colleagues, friends, or other contacts and share their contact
				information and other personal information with us. Please do not provide us with someone’s
				personal information unless you have their permission to do so.
			</li>
		</ul>

		<h2 className="wp-block-heading" id="USE-OF-AND-PURPOSES-FOR-PROCESSING-PERSONAL-INFORMATION">
			<strong>USE OF AND PURPOSES FOR PROCESSING PERSONAL INFORMATION</strong>
		</h2>

		<p>
			Memphis Arena Affiliates’ primary purpose in collecting personal information is to provide the
			Platform or Event and to operate our business. We also use personal information for the
			purposes set forth below and as otherwise described in this Privacy Policy or at the time of
			collection.
		</p>

		<p>
			<strong>To provide the Platform or Event</strong>. We may use personal information to operate
			our business. For example, we use personal information to:
		</p>

		<ul className="wp-block-list dot">
			<li>
				process your transactions, including purchasing Products or in-person services when you
				visit FedExForum or attend an Event;
			</li>

			<li>establish, manage, monitor, and maintain your account; </li>

			<li>improve and personalize your experience on or with the Platform or at an Event;</li>

			<li>
				verify your identity for authentication and security purposes, or determine your eligibility
				for offers and promotions;
			</li>

			<li>help us return lost property to its rightful owner;</li>

			<li>manage access to specific areas at FedExForum or at an Event;</li>

			<li>
				to provide you with appropriate services (for example, accessibility needs or dietary
				preferences at an Event);
			</li>

			<li>
				communicate with you, including providing notices about an Event or your purchase or
				transaction, and responding to any of your requests, feedback, or questions;
			</li>

			<li>manage affiliated sports clinics, competitions, and other community events; </li>

			<li>provide any contests, surveys, sweepstakes, or other promotions you participate in;</li>

			<li>provide maintenance and support; and</li>

			<li>fulfill any other purpose for which you provide personal information.</li>
		</ul>

		<p>
			<strong>Research and development</strong>. We use personal information for research and
			development purposes, including to study and improve the features and functionality of our
			Platform and our business, understand and analyze the usage trends and preferences of our
			Users, and develop and improve our Content and Services. As part of these activities, we may
			create aggregated, de-identified, or other anonymous data from personal information we
			collect. We make personal information into anonymous data by removing information that makes
			the data personally identifiable to you. We may use this anonymous data and share it with
			third parties for our lawful business purposes.
		</p>

		<p>
			<strong>Direct marketing</strong>. We may send you Memphis Arena Affiliates-related or other
			direct marketing communications as permitted by law, including materials, updates,
			information, special offers, and promotional material from us, our Related Entities, and our
			Event Partners. You may opt-out of our marketing communications as described in the “
			<a href="#YOUR-CHOICES">YOUR CHOICES</a>” section below. 
		</p>

		<p>
			<strong>Interest-based advertising</strong>. We may work with third-party advertising
			companies and social media companies to help us advertise our business and to display ads for
			our Platform, Related Entities, Products, and Events. These companies may use cookies and
			similar technologies to collect information about you (including the online activity
			information and device information described above in the section called “Personal Information
			Automatically Collected”) over time across our Platform and other websites and services or
			your interaction with our emails, and use that information to serve ads that they think will
			interest you. In addition, some of these companies may use non-cookie-based information, such
			as hashed customer lists that we share with them, to deliver ads to you and to similar users
			on their platforms on our behalf. For Users of our App, where permitted by law, we may also
			track your device’s precise geolocation to serve and display advertising relevant to your
			location. This is typically done based on uniquely identifying your internet device. You can
			learn more about <a href="#YOUR-CHOICES">your choices</a> for limiting interest-based
			advertising, including turning off access to geolocation information, in the “
			<a href="#YOUR-CHOICES">YOUR CHOICES</a>” section below. 
		</p>

		<p>
			<strong>Training AI systems</strong>.&nbsp;We may use your personal information to train
			artificial intelligence (AI) systems to develop new features, functionality, products, or
			services. This enables us to provide more accurate and helpful services to our Users over
			time.<strong>&nbsp;</strong>
		</p>

		<p>
			<strong>Compliance, fraud prevention, and safety</strong>. We may use personal information and
			disclose it to law enforcement, government authorities, and private parties as we believe
			necessary or appropriate to: (a) maintain the safety, security, and integrity of the Platform,
			the Event, and our business, databases, and other technology assets; (b) protect our, your, or
			others’ rights, privacy, safety or property (including by making and defending legal claims or
			in response to health emergencies or an incident at FedExForum or at an Event); (c) audit our
			internal processes for compliance with legal and contractual requirements and internal
			policies; (d) enforce the terms and conditions that govern the Platform, your transaction, or
			attendance at FedExForum or at an Event; and (e) prevent, identify, investigate, and deter
			fraudulent, harmful, unauthorized, unethical or illegal activity, including cyberattacks and
			identity theft. We will also use personal information as we believe necessary or appropriate
			to comply with applicable laws, lawful requests, and legal process, such as to respond to
			subpoenas or requests from government authorities.
		</p>

		<p>
			<strong>With your consent</strong>. We will disclose your personal information in accordance
			with your prior direction or, in some cases, we may specifically ask you for your consent to
			collect, use, or share your personal information, such as when required by law.
		</p>

		<h2 className="wp-block-heading" id="DISCLOSURE-OF-PERSONAL-INFORMATION">
			<strong>DISCLOSURE OF PERSONAL INFORMATION</strong>
		</h2>

		<p>
			In addition to the specific situations discussed elsewhere in this Privacy Policy or at the
			point of collection, we may share your personal information with the following entities or
			persons:
		</p>

		<p>
			<strong>Related Entities</strong>. We may share your personal information with our holding
			company, affiliates, subsidiaries, and other related companies for the purposes described in
			this Privacy Policy, including to cross-promote our Related Entities or other Events that we
			believe may be of interest to you.
		</p>

		<p>
			<strong>Event Partners</strong>. We may share your personal information with Event Partners to
			provide an Event, such as third-party ticketing platforms or box offices, venue owners,
			facility operators and management companies, sponsors, performers, promoters, concession or
			retail vendors, and other third parties that can provide added value to the Event. In certain
			circumstances, you may direct us to disclose your personal information to or interact with
			these Event Partners as part of attending an Event, visiting the respective venue or facility,
			or making a purchase. However, in other cases, these Event Partners may also receive your
			personal information from us because we think their products or services may be of interest to
			you.&nbsp;
		</p>

		<p>
			<strong>The League</strong>. Depending on how you engage with the Platform, we may share
			certain of your personal information with the League, through its subsidiary NBA Media
			Ventures, LLC, and the League may further share such information with its other Member Clubs.
			Such information may include account information, online activity information, browser
			&amp;device information, and other information relating to your use of the Platform. The
			League uses such information to conduct analysis to better understand fans and fan engagement
			across the League and its other Member Clubs, and other uses described in the{" "}
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://www.nba.com/privacy-policy&amp;g=YjQ3MmI1YTU2YzgwMWU3YQ==&amp;h=NTk2Yzg4MDU5ZTg3OTliZThiZjVjMTdlY2IwMGRjYTRmMjBiZWJmODc0YjBiZmE1ODEyYTQxYzVlMDczNzJjYQ==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				League’s privacy policy
			</a>
			. The League may also use and share insights to enable the League and its other Member Clubs
			to customize and improve their products and services, or advertising and communications.
			Memphis Arena Affiliates are not responsible for the privacy practices of the League or its
			other Member Clubs. You may opt-out of this sharing of your personal information with the
			League as described in the “<a href="#YOUR-CHOICES">YOUR CHOICES</a>” section below.
		</p>

		<p>
			<strong>Service Providers</strong>. We may share your personal information with third parties
			who perform services on our behalf that are necessary for the orderly operation of our
			business, the Platform, or an Event. For example, we may disclose your geolocation information
			to service providers that perform certain functions or services on our behalf. If you wish to
			stop the further collection of your geolocation information, please opt out using your device
			settings as described in the “<a href="#YOUR-CHOICES">YOUR CHOICES</a>” section below. We also
			work with service providers that help us perform website hosting, e-commerce, payment
			processing, ticket fulfillment, maintenance services, database management, analytics, fraud
			protection, marketing, SMS, customer relationship management, finances, and other purposes.
		</p>

		<p>
			<strong>Advertising Partners</strong>. We may share personal information with third parties
			who we partner with for advertising campaigns or that collect information about your activity
			on the Platform for the purposes described in the “Interest-Based Advertising” section above.
			We may also disclose your geolocation information to third parties so that they may provide
			you with geographically relevant advertising. If you wish to stop the further collection of
			your geolocation information, please opt out using your device settings as described in the “
			<a href="#YOUR-CHOICES">YOUR CHOICES</a>” section below. Please note, if you opt in to our
			text messaging program, text messaging originator opt-in data and consentis not shared with
			any third parties for their own marketing purposes.
		</p>

		<p>
			<strong>Sweepstakes and Joint Marketing Partners</strong>. We may also share your personal
			information with other partners in order to provide you with content and other features
			through the Platform, and such partners may send you promotional materials or otherwise
			contact you regarding products and services that they offer. When you choose to enter a
			contest or sign up for a sweepstakes, we may share the personal information you provide as
			part of the offer with the named co-sponsors or other third parties affiliated with such
			offer.&nbsp;
		</p>

		<p>
			<strong>Other Users and the Public</strong>.&nbsp;You may be able to submit content to the
			Platform (such as comments, questions, stories, reviews, surveys, blogs, photos, and videos),
			or may win a contest/sweepstakes that we may publicly post, and we may identify you by
			displaying information such as your name, username, or social media handle along with the
			content you submit. By attending FedExForum or an Event, you may be captured by video which
			may be shared publicly. We do not control how other Users or third parties use any personal
			information that you make available to other Users or the public.
		</p>

		<p>
			<strong>Professional Advisors</strong>. We may share personal information with persons,
			companies, or professional firms providing Memphis Arena Affiliates with advice and consulting
			in accounting, administrative, legal, tax, financial, debt collection, and other matters.
		</p>

		<p>
			<strong>Authorities and Others for Compliance, Fraud Prevention, and Safety</strong>. Under
			certain circumstances, we may be required to disclose personal information to law enforcement,
			government authorities, and other parties if required to do so by law or in response to valid
			requests by public authorities (e.g., a court or a government agency). We may disclose
			personal information in the good faith belief that such action is necessary to comply with a
			legal obligation or for the purposes described above in the section titled “Compliance, fraud
			prevention, and safety.”
		</p>

		<p>
			<strong>Business Transaction Participants</strong>. We may disclose personal information to
			third parties in connection with any business transaction (or potential transaction) involving
			a merger, acquisition, sale of shares or assets, financing, consolidation, reorganization,
			divestiture, or dissolution of all or a portion of our business (including in connection with
			a bankruptcy or similar proceedings).
		</p>

		<h2 className="wp-block-heading" id="INTERNATIONAL-TRANSFERS-OF-PERSONAL-INFORMATION">
			<strong>INTERNATIONAL TRANSFERS OF PERSONAL INFORMATION</strong>
		</h2>

		<p>
			Memphis Arena Affiliates are based in the United States, and we have service providers in the
			United States and potentially other countries. Your personal information may be collected,
			used, and stored in these countries or other locations outside of your home country. Privacy
			laws in the locations where we handle your personal information may not be as protective as
			the privacy laws in your home country. By providing your personal information, where
			applicable law permits, you specifically and expressly consent to such transfer and processing
			and the collection, use, and disclosure set forth herein.
		</p>

		<h2 className="wp-block-heading" id="SECURITY-OF-PERSONAL-INFORMATION">
			<strong>SECURITY OF PERSONAL INFORMATION</strong>
		</h2>

		<p>
			No method of transmission over the internet, or method of electronic storage, is fully secure.
			While we use commercially reasonable efforts to protect your personal information from the
			risks presented by unauthorized access or acquisition, we cannot guarantee the security of
			your personal information.
		</p>

		<h2 className="wp-block-heading" id="YOUR-CHOICES">
			<strong>YOUR CHOICES&nbsp;</strong>
		</h2>

		<p>
			You can make the following choices regarding your personal information. For Users who engage
			with the Memphis Grizzlies or Memphis Hustle, please note that the League also provides
			certain choices in connection with the League’s processing of your personal information. To
			learn more about the League’s privacy practices and the rights you may have related to your
			personal information, please visit the NBA.com Privacy Policy, available at{" "}
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://www.nba.com/privacy-policy&amp;g=YjQ3MmI1YTU2YzgwMWU3YQ==&amp;h=NTk2Yzg4MDU5ZTg3OTliZThiZjVjMTdlY2IwMGRjYTRmMjBiZWJmODc0YjBiZmE1ODEyYTQxYzVlMDczNzJjYQ==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				https://www.nba.com/privacy-policy
			</a>
			.&nbsp;
		</p>

		<p>
			<strong>Review and Request Changes to Your Personal Information</strong>. If you have an
			account with us, we rely on you to update and correct the personal information contained in
			your account. If the Website or Service does not permit you to update or correct certain
			personal information, please submit a request to us using the contact information listed at
			the end of this Privacy Policy. Note that we may keep historical information in our backup
			files as permitted by law.
		</p>

		<p>
			<strong>Marketing Emails</strong>. You may opt-out of marketing-related emails by clicking the
			“unsubscribe” link at the bottom of the email or by <a href="#CONTACTING-US">contacting us</a>{" "}
			as provided at the end of this Privacy Policy. You may still receive service-related
			communications, such as those relating to your account, order, or other request.
		</p>

		<p>
			<strong>Text Messages</strong>.You may opt-out of receiving text messages from us at any time
			by texting the word “STOP” in reply to any text message from us using the mobile device that
			is receiving the messages.&nbsp;
		</p>

		<p>
			<strong>Device Geolocation Tracking</strong>. If you wish to stop the further collection of
			your geolocation information, please opt out using your device settings. For Android users,
			please see the following link for instructions on how to opt-out of or withdraw consent to
			geolocation data collection from your settings:{" "}
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://support.google.com/accounts/answer/6179507?hl=en&amp;g=YjI2YWIyOGYzMjI2YWU1NA==&amp;h=NWQyOTQyYzk3NjY4ZjVkNmFjZTZjNzQ3NzE0ZTEyNzBjYWFkNGI2ODdmN2E3ZTMyYmY1M2VhM2U3ZDZkOTA5Mw==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjIxZTU1OTQ3MWI3YTM4Y2Q5ZDI2MGE1YWUxZGM1ZGQ1OnYxOnA6Rg==">
				https://support.google.com/accounts/answer/6179507?hl=en
			</a>
			. For iOS users, please see the following link for instructions on how to opt-out of or
			withdraw consent to geolocation data collection from your settings:&nbsp;
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://support.apple.com/en-us/HT207092&amp;g=ZjY2Mjg0OThkOTQ3NjU1ZA==&amp;h=ZTc3MjFlMjM1ZGFjNWQxYzMxYWYzNWQ5YzFmMzJiYzNjMWM4N2M4MmRiNjQzZGZiOGI3NjdhMjI5MGE0MDc5OQ==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjIxZTU1OTQ3MWI3YTM4Y2Q5ZDI2MGE1YWUxZGM1ZGQ1OnYxOnA6Rg==">
				https://support.apple.com/en-us/HT207092
			</a>
			. Turning off access to geolocation and precise geolocation data may disable or interfere with
			certain functionality or services.
		</p>

		<p>
			<strong>Sharing with the League</strong>. We may share your personal information with the
			League as described in this Privacy Policy. If you would like to opt-out of sharing your
			personal information with the League, please email us at the address listed at the end of this
			Privacy Policy.
		</p>

		<p>
			<strong>Cookies</strong>. Most browsers let you remove or stop accepting cookies from the
			websites you visit. To do this, follow the instructions in your browser’s settings. Many
			browsers accept cookies by default until you change your settings. If you do not accept
			cookies, however, you may not be able to use all functionality of the Platform and our Website
			may not work properly. For more information about cookies, including how to see what cookies
			have been set on your browser and how to manage and delete them, visit{" "}
			<a href="https://checkpoint.url-protection.com/v1/url?o=http://www.allaboutcookies.org&amp;g=OWRlYjFhNWRjNzAyMjI1YQ==&amp;h=OTg2Y2FjZTg5MDBmZDA5NjllMmUzMjQwMTVlYzFkMjc5YWNkYTQ5ZGRhZjA3ZDEyMWIzMzBkODNiY2M0MDk0YQ==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				www.allaboutcookies.org
			</a>
			.&nbsp;
		</p>

		<p>
			<strong>Analytics.</strong> We use Google Analytics to help us analyze how our Website is
			being accessed and used. You can learn more about Google Analytics cookies by clicking{" "}
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://developers.google.com/analytics/resources/concepts/gaConceptsCookies&amp;g=OGIyMjQ5NjNhMDQyZTBmOA==&amp;h=ZjZhOTY3YjgxYjYyOTYwZWM2YTllZTJiYmJkYTJkOGZjMzgzY2NkYjI4Yzg3ODNkZmNlMmI4OTc1YTZhZWZlZA==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				here
			</a>{" "}
			and about how Google protects your data by clicking{" "}
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://support.google.com/analytics/answer/6004245&amp;g=ODVjZTcxNDkwODJmZjAxMQ==&amp;h=YzIyOTVmNjBhMWEzODU3YWEyMWVkNDdjM2FlMTM3ZjYwMmZjYzQ0ODM5ZGExNGE0ZDgwNWFjMTcwZTdiNjE4Yw==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				here
			</a>
			. To opt-out of Google Analytics, you can download and install the Google Analytics Opt-out
			Browser Add-on, available{" "}
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://tools.google.com/dlpage/gaoptout?hl=en&amp;g=ZTNhZDI4Yjc2NWJlZjE3OQ==&amp;h=NTllMDdhZjAwZWVmZmVhMWIzOGFiYWQ5YTFjM2Q3MTZjN2IwMTVlMTM2NWM2MmU4ZTZjZDFkNGJjOTg3YTY0OQ==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				here
			</a>
			.&nbsp;
		</p>

		<p>
			<strong>Advertising Choices</strong>. You can limit the use of your information for
			interest-based advertising by blocking third-party cookies in your browser settings, using
			browser plug-ins/extensions, and/or using your mobile device settings to limit the use of the
			advertising ID associated with your mobile device. You can also opt out of interest-based ads
			from companies participating in the following industry opt-out programs by visiting the linked
			websites: the Network Advertising Initiative (
			<a href="https://checkpoint.url-protection.com/v1/url?o=http://www.networkadvertising.org/managing/opt_out.asp&amp;g=YjllZTNjODNkYTdlODBhNQ==&amp;h=NmZjMTVkYjdmNmNiMjQ5YWUyYjdkZjQ1N2NjY2I0ZDI5Mjg5NDBmNDU5OTU3YTNhMjAyOTJmNjBhZDZiZjYxZQ==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				http://www.networkadvertising.org/managing/opt_out.asp
			</a>
			), and the Digital Advertising Alliance (
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://optout.aboutads.info&amp;g=OWQyNTFmMmEyODc3YWViNA==&amp;h=ZTAwNjdlMzU3ZDlmM2I3Yjc5N2ZjMTM5MmE5ZTUwYzI3ZDQ2NGNkNzBlYmY0ODJlNzFlMWVhZGQxNDc5MGU0OQ==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				https://optout.aboutads.info
			</a>
			). Users of our App may opt out of receiving targeted advertising in mobile apps through
			participating members of the Digital Advertising Alliance by installing the AppChoices mobile
			app, available
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://www.youradchoices.com/appchoices&amp;g=ZjQ4MjQ2ZGQyYmQ1M2NlMA==&amp;h=MmQ4MjgxYWQ3Zjc4ZWZhNTVhNDRkMjgxYTcxZTNhMzJkMzVhYTFmNjI3YmU1YTkzYjc1N2ZmY2NlNzgzYzQ2Mw==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				&nbsp;here
			</a>
			, and selecting the user’s choices. Some of the companies we work with may offer their own
			opt-out mechanisms. For example, you can learn more about how Google uses cookies for
			advertising purposes by clicking{" "}
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://policies.google.com/technologies/ads&amp;g=MjM2N2JlNGYxMDlkNDRjZA==&amp;h=NmEyZmQzZGUzNzljZTRlOGVhMjIzZDA2NmUzNmU4NWI4NmEzMjFhYmU3Njc3YzQ4YzRiYmU4MWNhZDhkYmQzYw==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				here
			</a>{" "}
			and opt-out of ad personalization by Google by clicking{" "}
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://adssettings.google.com/authenticated?hl=en&amp;g=ZDQ1NTc5MTRkZjViNzcwYw==&amp;h=ZjZjNmNlOGY5MmQzMzI1Y2NlMTUzYzIxZGM3ZWZlMjNhN2IzZTI0NzliZWU0NzdhNjU5OWVjZThiYzRiZDQ5Nw==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				here
			</a>
			.
		</p>

		<p>
			Many of the opt-out preferences described in this section must be set on each device or
			browser for which you want them to apply. Please note that some of the advertising companies
			we work with may not participate in the opt-out mechanisms described above, so even after
			opting-out, you may still receive interest-based advertisements from other companies. If you
			opt-out of interest-based advertisements, you will still see advertisements online but they
			may be less relevant to you.
		</p>

		<p>
			<strong>Do Not Track</strong>. Some browsers may be configured to send “Do Not Track” signals
			to the online services that you visit. The Website currently does not respond to “Do Not
			Track” signals. To find out more about “Do Not Track,” please visit{" "}
			<a href="https://checkpoint.url-protection.com/v1/url?o=http://www.allaboutdnt.com&amp;g=OWNhZWJmZGI0MDFlMjk4OA==&amp;h=NWQyZGNkZjdiNzUzMDFhMDM0MTUxOWRkYWQyNjExZTcyZDcxZTkxMDZmYTBhY2QwNzYxNjAwYzZhMTAwNzllZg==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				http://www.allaboutdnt.com
			</a>
			.&nbsp;
		</p>

		<h2 className="wp-block-heading" id="THIRD-PARTY-SITES-AND-SERVICES">
			<strong>THIRD PARTY SITES AND SERVICES</strong>
		</h2>

		<p>
			Our Platform may contain links to other websites, apps, and online services operated by third
			parties. These links are not an endorsement of, or representation that we are affiliated with,
			any third party. In addition, our content may be included on web pages or online services that
			are not associated with us. We do not control third party websites or online services, and we
			are not responsible for their actions. Other websites and services follow different rules
			regarding the collection, use, and sharing of your personal information. We encourage you to
			read the privacy policies of the other websites and online services you use.
		</p>

		<h2 className="wp-block-heading" id="ADDITIONAL-NOTICE-TO-RESIDENTS-OF-APPLICABLE-U.S.-STATES">
			<strong>ADDITIONAL NOTICE TO RESIDENTS OF APPLICABLE U.S. STATES</strong>
		</h2>

		<p>
			The following disclosures supplement the information provided elsewhere in this Privacy Policy
			and apply to residents of certain U.S. states where local law requires us to provide
			additional information about our processing activities, including California, Nebraska,
			Tennessee, and Texas. For purposes of this section, “personal information” means “personal
			information” as such term is defined under applicable U.S. state privacy law but does not
			include information exempted from the scope of such law.&nbsp;
		</p>

		<p>
			We describe the categories of personal information we may collect, and have collected in the
			past 12 months, and the categories of sources from which we collect personal information, in
			the section above titled “
			<a href="#COLLECTION-OF-PERSONAL-INFORMATION">COLLECTION OF PERSONAL INFORMATION</a>.” This
			includes sensitive data as defined under applicable U.S. privacy law in the form of precise
			geolocation collected from Users of the App. 
		</p>

		<p>
			The purposes, including the specific business and commercial purposes, for which we collect
			and use these categories of personal information are described in the section titled “
			<a href="#USE-OF-AND-PURPOSES-FOR-PROCESSING-PERSONAL-INFORMATION">
				USE OF AND PURPOSES FOR PROCESSING PERSONAL INFORMATION
			</a>
			.” 
		</p>

		<p>
			We may disclose (and in the past twelve months may have disclosed) any of the personal
			information referenced above to the entities or persons described in the section titled “
			<a href="#DISCLOSURE-OF-PERSONAL-INFORMATION">DISCLOSURE OF PERSONAL INFORMATION</a>.” 
		</p>

		<p>
			In addition, we may disclose (and in the past twelve months may have disclosed) certain
			Contact Information, Professional Information, Account Information, Transaction History,
			Demographic Information, Preferences (including inferences), Survey Responses, Online Activity
			Information, Device Information, and Communications to our advertising partners for purposes
			of advertising our Platform, Products, and Events, including through interest-based
			advertising, and tracking the success of our advertising campaigns and measuring activity on
			the Platform. During this same period, depending on how you engage with the Platform we may
			have also disclosed Contact Information, Account Information, Transaction History, Demographic
			Information, and Preferences (including inferences) to the League, Event Partners, and
			sweepstakes and joint marketing partners for such third parties own commercial use, each as
			further described in this Privacy Policy under the section titled “USE OF PERSONAL
			INFORMATION.”&nbsp;
		</p>

		<p>
			These disclosures may be considered a “sale” of personal information under applicable U.S.
			privacy laws because we receive monetary or other valuable consideration in exchange for these
			disclosures, or such disclosures are for “targeted advertising.” Residents of applicable U.S.
			states may opt-out of these disclosures as described below.&nbsp;
		</p>

		<p>
			We do not knowingly “sell” or process for targeted advertising the personal information of
			residents under 16 years of age.
		</p>

		<p>
			<strong>Retention of Personal Information</strong>.We will retain your personal information
			for as long as necessary to fulfill the purpose of collection, including for the purposes of
			satisfying any legal, accounting, or reporting requirements, to establish and defend legal
			claims, for fraud prevention purposes, or as long as required to meet our legal obligations.
			To determine the appropriate retention period for personal information, we consider the
			amount, nature, and sensitivity of the personal information, the potential risk of harm from
			unauthorized use or disclosure of your personal information, the purposes for which we process
			your personal information and whether we can achieve those purposes through other means, and
			the applicable legal requirements.
		</p>

		<p>
			<strong>Your Privacy Rights</strong>.&nbsp; This section describes the rights you may have
			under applicable U.S. privacy laws depending on the state where you live, and how you exercise
			them. Please note, these rights are not absolute, and, in some cases, we may not be able to
			respond to your request, such as when a legal exemption applies or if we are not able to
			verify your identity.
		</p>

		<p>
			For Users who engage with the Memphis Grizzlies or Memphis Hustle, please note that the League
			also provides certain choices in connection with the League’s processing of personal
			information. To learn more about the League’s privacy practices and the rights you may have
			related to your personal information, please visit the NBA.com Privacy Policy, available at{" "}
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://www.nba.com/privacy-policy&amp;g=YjQ3MmI1YTU2YzgwMWU3YQ==&amp;h=NTk2Yzg4MDU5ZTg3OTliZThiZjVjMTdlY2IwMGRjYTRmMjBiZWJmODc0YjBiZmE1ODEyYTQxYzVlMDczNzJjYQ==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				https://www.nba.com/privacy-policy
			</a>
			, or click the “Your Privacy Choices” link at the bottom of the NBA.com website.&nbsp;
		</p>

		<ul className="wp-block-list dot">
			<li>
				<strong>Right to Know</strong>. You can request that we confirm whether we process your
				personal information or, in some cases, to disclose to you what categories of personal
				information we have collected about you, the categories of sources from which we collected
				your personal information, the business or commercial purpose for which your personal
				information was collected, sold, shared, or disclosed, and whether and how we share,
				disclose and/or sell your personal information to third parties.
			</li>

			<li>
				<strong>Right to Access/Data Portability</strong>. You can request a copy of the personal
				information we have collected about you.
			</li>

			<li>
				<strong>Right to Delete</strong>. You can request that we delete the personal information we
				have about you.
			</li>

			<li>
				<strong>Right to Correct</strong>. You can request that we correct inaccurate personal
				information that we maintain about you.
			</li>

			<li>
				<strong>Right to Opt-Out of Sales</strong>. If we “sell” your personal information, you can
				opt-out. 
			</li>

			<li>
				<strong>
					Right to Opt-Out of Targeted Advertising or Sharing for Cross-Context Behavioral
					Advertising
				</strong>
				. You can request to opt-out of the sharing or use of your personal information for
				targeted/cross-context behavioral advertising purposes.
			</li>

			<li>
				<strong>Right to Limit / Withdraw Consent</strong>.You can withdraw your consent or request
				us to limit the use or disclosure of your sensitive personal information to only certain
				business purposes. 
			</li>

			<li>
				<strong>Right to Nondiscrimination</strong>. You are entitled to exercise the rights
				described above without receiving discriminatory treatment. 
			</li>
		</ul>

		<p>
			<strong>How to Exercise Your Rights</strong>. You may exercise the rights described above as
			follows:
		</p>

		<ul className="wp-block-list dot">
			<li>
				<strong>Know, Access, Delete, or Correct</strong>. You may exercise these privacy rights by
				completing the interactive webform available by clicking here:{" "}
				<a href="https://checkpoint.url-protection.com/v1/url?o=https://fedexforum.com/ccpa-request-form&amp;g=ODU4ZTM3OTMxMzBjYzMwYw==&amp;h=MjViYjA3OThmNDdmYTEwYzNmZDE4YTg4ZmY2ODFiOTc0MjYwYmJkZDQ3MWUxMDQwZTc2YTAxYjE0YmQ1M2VkMg==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
					Your Privacy Choices
				</a>
				, or by submitting your request to the email address listed at the end of this Privacy
				Policy with “Privacy Request” in the subject line.
			</li>

			<li>
				<strong>Right to Limit / Withdraw Consent</strong>. You may withdraw your consent with
				respect to precise geolocation information using your device settings, as described in this
				Privacy Policy under the section titled, “<a href="#YOUR-CHOICES">YOUR CHOICES</a>
				.”
			</li>

			<li>
				<strong>Opt-Out of Sales</strong>.You may request to opt-out out of “sales” of your personal
				information, such as to the League, Event Partners, and other third parties, by completing
				the interactive webform available by clicking here:{" "}
				<a href="https://checkpoint.url-protection.com/v1/url?o=https://fedexforum.com/ccpa-request-form&amp;g=ODU4ZTM3OTMxMzBjYzMwYw==&amp;h=MjViYjA3OThmNDdmYTEwYzNmZDE4YTg4ZmY2ODFiOTc0MjYwYmJkZDQ3MWUxMDQwZTc2YTAxYjE0YmQ1M2VkMg==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
					Your Privacy Choices
				</a>
				.
			</li>

			<li>
				<strong>
					Opt-Out of Targeted Advertising or Sharing for Cross-Context Behavioral Advertising
				</strong>
				. To opt-out your personal information from cross-context behavioral advertising / targeted
				advertising through the use of cookies, please select your cookie preferences as provided
				through a link at the bottom of the Website, or by broadcasting the Global Privacy Control
				(GPC) signal.  Note that due to technological limitations, if you visit our Website from a
				different computer or device, or clear cookies on your browser that store your preferences,
				you will need to return to the Website to select your preferences or rebroadcast the GPC
				signal. For other non-cookie disclosures of your personal information for targeted
				advertising or cross-context behavioral advertising, please complete the interactive webform
				available by clicking here:{" "}
				<a href="https://checkpoint.url-protection.com/v1/url?o=https://fedexforum.com/ccpa-request-form&amp;g=ODU4ZTM3OTMxMzBjYzMwYw==&amp;h=MjViYjA3OThmNDdmYTEwYzNmZDE4YTg4ZmY2ODFiOTc0MjYwYmJkZDQ3MWUxMDQwZTc2YTAxYjE0YmQ1M2VkMg==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
					Your Privacy Choices
				</a>
				.  
			</li>
		</ul>

		<p>
			<strong>
				To exercise your right to opt-out through the use of cookies on the Memphis Grizzlies and
				Memphis Hustle Websites, please select the “Your Privacy Choices” link at the bottom of the
				Website or visit the{" "}
			</strong>
			<a href="https://checkpoint.url-protection.com/v1/url?o=https://www.nba.com/privacy-policy&amp;g=YjQ3MmI1YTU2YzgwMWU3YQ==&amp;h=NTk2Yzg4MDU5ZTg3OTliZThiZjVjMTdlY2IwMGRjYTRmMjBiZWJmODc0YjBiZmE1ODEyYTQxYzVlMDczNzJjYQ==&amp;p=YzJ1Om1lbXBoaXNncml6emxpZXM6YzpvOjg2N2IyZmE0M2RhYjA0M2VkNzI3MDNhNjFmNDZjNGVhOnYxOnA6Rg==">
				<strong>League’s privacy policy</strong>
			</a>
			<strong>.&nbsp; </strong>
			<strong>&nbsp;</strong>
		</p>

		<p>
			We reserve the right to confirm your residence to process your requests and may need to
			confirm your identity to process your requests. This is a security measure to, for example,
			help ensure we do not disclose information to a person who is not entitled to receive it. The
			identity verification process may vary depending on the type of request and how you submit
			your request.&nbsp;
		</p>

		<p>
			Where permitted under applicable law, you may also designate an authorized agent to make a
			request on your behalf. If you do so, we may require proof of your identification, the
			authorized agent’s proof of identification, and any other information that we may request in
			order to verify the request, including evidence of valid permission for the authorized agent
			to act on your behalf. We cannot process your request if you do not provide us with sufficient
			detail to allow us to understand and respond to it.
		</p>

		<p>
			You will not have to pay a fee to access your personal information (or to exercise any of the
			other rights).&nbsp; However, we may charge a reasonable fee or decline to comply with your
			request if your request is clearly unfounded, repetitive, or excessive.
		</p>

		<p>
			We try to respond to all legitimate requests within 45 days (or such shorter period as may be
			required under applicable law) of your request. Occasionally it may take us longer than 45
			days to respond, for instance if your request is particularly complex or you have made a
			number of requests. In this case, we will notify you of the delay, and may continue to update
			you regarding the progress of our response.
		</p>

		<p>
			<strong>Appeal of Privacy Rights Decisions</strong>.If you wish to appeal our denial of your
			request, you may email us at{" "}
			<a href="mailto:legaldatarequest@grizzlies.com">legaldatarequest@grizzlies.com</a>. We will
			process your appeal in accordance with applicable state privacy law.
		</p>

		<p>
			<strong>California “Shine the Light Law.” </strong>If you direct us not to “sell” or use your
			personal information for targeted advertising or sharing for cross-context behavioral
			advertising, we will consider it a request pursuant to California’s “Shine the Light” law to
			stop sharing your personal information covered by that law with third parties for their direct
			marketing purposes.
		</p>

		<p>
			<strong>Nevada Opt-Out</strong>. Nevada Revised Statutes Chapter 603A allows Nevada residents
			to opt-out of the “sale” of certain types of personal information. Subject to several
			exceptions, Nevada law defines “sale” to mean the exchange of certain types of personal
			information for monetary consideration to another person.&nbsp;If you are a Nevada resident
			who wishes to exercise your “sale” opt-out rights, please submit your request to the email
			address listed at the end of this Privacy Policy with “Nevada Opt-Out” in the subject line.
		</p>

		<h2 className="wp-block-heading" id="CONTACTING-US">
			<strong>CONTACTING US</strong>
		</h2>

		<p>
			If you have any questions or concerns about our Privacy Policy or any other privacy or
			security issue, please contact us at{" "}
			<a href="mailto:legaldatarequest@grizzlies.com">legaldatarequest@grizzlies.com</a>.&nbsp;
			&nbsp;
		</p>
	</div>
)

export default PrivacyPolicy
